import { z } from "zod";
import { defineStepConfig } from "../registry/helpers";

/**
 * Background Agent Step
 *
 * Executes a selected background AI agent definition and returns
 * the structured output produced by that agent.
 */
export const backgroundAgentStep = defineStepConfig({
  type: "background_agent",
  name: "Background Agent",
  description:
    "Runs a selected background AI agent definition with dynamic input and output fields",
  category: "AI & Agents",

  configSchema: z.object({
    agentDefinitionId: z.uuid(),
    procedures: z
      .array(z.uuid())
      .default([])
      .describe(
        "Procedures added to the agent definition's own allowlist for runs started from this node. A non-empty list grants the agent's browser tool its procedure tools even when the definition has procedures switched off.",
      ),
  }),

  inputSchema: z.object({}).passthrough(),

  outputSchema: z.object({}).passthrough(),

  emits: [] as const,

  execution: {
    // Background agents can run long (remote desktop sessions, multi-step reasoning, etc.)
    startToCloseTimeout: "2 hours",
    // Retry on crash/deploy so Temporal reschedules on a live worker rather than
    // permanently failing the step. A retry resumes the interrupted agent run
    // from its recorded steps: only replay-safe (read) tools that were in flight
    // run again; an unsafe one is reported to the model as interrupted. The task
    // loop also re-claims orphaned in_progress tasks and resumes their runs the
    // same way. Non-retryable errors (guardrail blocks) still fail immediately.
    retry: {
      maximumAttempts: 3,
      initialInterval: "10 seconds",
      backoffCoefficient: 2,
      maximumInterval: "2 minutes",
    },
    nonRetryableErrorTypes: [
      "WorkflowGuardrailBlockedError",
      "WorkflowGuardrailReviewRequiredError",
    ],
  },
});
